import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  Input,
  SimpleChanges,
} from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import {
  AgGridEvent,
  ColDef,
  ColumnMovedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
} from 'ag-grid-community';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

// CONFIGS
import { PaginationConfig } from '../../../shared/configs/';

// INTERFACES
import {
  IPagination,
  IWarehouseGroup,
  LocalStorageKeys,
} from '../../../shared/interfaces';

// PIPES
import { CapitalizePipe } from '../../../shared/pipes/capitalize.pipe';

// SERVICES

// UTILS
import { trackById, trackByLabel } from '../../../shared/util/track-by.util';
import { StockAgTableConfig } from '../../configs';
import {
  EStockStatusValue,
  IStock,
  IStockGroup,
} from '../../interfaces/stock.interface';

// COMPONENTS
import { StockGroupColumnComponent } from '../stock-group-column/stock-group-column.component';

import { StockFacadeService } from './../../../shared/services/facades/stock/stock.facade.service';

@Component({
  selector: 'lum-stock-ag-table',
  templateUrl: './stock-ag-table.component.html',
  styleUrls: ['./stock-ag-table.component.scss'],
  providers: [],
})
export class StockAgTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  currentStockStatus :string | null;

  @Input()
  currentCustomFilter :any | null;

  @Input()
  warehouseGroups :IWarehouseGroup[] = [];

  columnDefs :ColDef[] = StockAgTableConfig.stockAgTableColumnDefs;
  defaultColDef :ColDef = StockAgTableConfig.defaultColDef;
  fields = StockAgTableConfig.fields;
  gridOptions :GridOptions | any = StockAgTableConfig.gridOptions;
  pageSizeOptions = StockAgTableConfig.pageSizeOptions;
  paginationConfig = PaginationConfig;
  paginationCurrentPage :number = 0;
  paginationPageSize :number = 100;
  relations = StockAgTableConfig.relations;

  groupByOptions :any[] = StockAgTableConfig.groupByColumns;

  toggledSidebar :boolean = false;
  isOpenedSidebar :boolean = false;

  private rowSelectionChanged$ = new Subject<void>();

  trackByLabel = trackByLabel;
  trackById = trackById;

  private onDestroy$ = new Subject();
  private gridApi! :GridApi;
  private gridColumnApi :any;

  constructor(
    private capitalizePipe :CapitalizePipe,
    private stockFacadeService :StockFacadeService,
  ) {}

  ngOnInit() :void {
    this.initColumnDefs();
    this.initGroupState();
    this.subscribeToStocks();
    this._subscribeToColumnFilters();
  }

  ngOnChanges({
    currentStockStatus,
    currentCustomFilter,
    warehouseGroups,
  } :SimpleChanges) :void {
    if (
      (currentStockStatus?.currentValue ||
        currentStockStatus?.currentValue === null) &&
      !currentCustomFilter?.currentValue
    ) {
      this.gridApi?.setFilterModel(this.storedPageState.filterModel);
      this.gridColumnApi?.applyColumnState({
        defaultState: { sort: null },
      });
    }

    if (warehouseGroups?.currentValue?.length) {
      this.updateWarehouseGroupColumns();
    }

    if (currentCustomFilter?.currentValue && !currentStockStatus?.currentValue) {
      const state = currentCustomFilter?.currentValue;

      if (state?.filterModel && Object.keys(state.filterModel)?.length) {
        this.gridApi?.setFilterModel(state.filterModel);
      }

      if (state?.columnState && Object.keys(state.columnState)?.length) {
        this.gridColumnApi.applyColumnState({
          state: state.columnState,
          applyOrder: true,
        });
      }
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onGridReady(params :GridReadyEvent<any>) :void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    this.applyStoredState();

    if (this.groupBy) {
      this.onGroupByChanged(this.groupBy);
    } else {
      this.stockFacadeService.filterChanged(
        {
          filterModel: this.currentFilterModel,
          columnState: this.currentColumnState,
        },
        {
          disableReload: true,
        },
      );
    }
  }

  toggleSidebar() :void {
    this.isOpenedSidebar = !this.isOpenedSidebar;

    localStorage.setItem(
      LocalStorageKeys.STOCK_AG_SIDEBAR_STATUS,
      JSON.stringify(this.isOpenedSidebar),
    );
  }

  onRowSelected(event :any) :void {
    if (!this.selectedRowCount) {
      this.isOpenedSidebar = true;
      localStorage.setItem(
        LocalStorageKeys.STOCK_AG_SIDEBAR_STATUS,
        JSON.stringify(true),
      );
    }

    const index = this.selectedRows.findIndex(
      (row) => row.uniqueId === event.data.uniqueId,
    );

    if (event.node.selected) {
      if (index === -1) {
        this.stockFacadeService.pushSelectedRow(event.data);
      }
    } else {
      if (index !== -1) {
        this.stockFacadeService.spliceSelectedRow(index);
      }
    }
  }

  isRowSelectable(node :any) :boolean {
    return node.data?.uniqueId;
  }

  onRowSelectionChanged() :void {
    this.rowSelectionChanged$.next();
  }

  onGroupByChanged(value :string) :void {
    this.removeColumnByField(this.groupBy);
    this.setRowGroupStatus({
      columnField: value ? value : this.groupBy,
      status: !!value,
    });

    this.gridOptions.autoGroupColumnDef = {
      ...this.gridOptions.autoGroupColumnDef,
      hide: false,
    };

    this.stockFacadeService.updateGroupingAndState(
      value,
      this.currentFilterModel,
      this.currentColumnState,
    );

    this.gridApi.refreshCells();
  }

  onPaginationChange(pagination :PageEvent) :void {
    this.stockFacadeService.paginationChange(pagination);
  }

  onFilterChanged(event :AgGridEvent) :void {
    this.stockFacadeService.filterChanged({
      filterModel: event.api.getFilterModel(),
      columnState: event.columnApi.getColumnState(),
    });
    this.gridApi.paginationGoToPage(1);
  }

  onSortChanged(event :AgGridEvent) :void {
    this.stockFacadeService.sortChanged({
      filterModel: event.api.getFilterModel(),
      columnState: event.columnApi.getColumnState(),
    });
    this.gridApi.paginationGoToPage(1);
  }

  onColumnVisibleChanged(event :AgGridEvent) :void {
    this.stockFacadeService.columnVisible({
      filterModel: event.api.getFilterModel(),
      columnState: event.columnApi.getColumnState(),
    });
  }

  onResetFilters() :void {
    this.stockFacadeService.resetFilters();
  }

  getRowHeight(params :any) :number | null {
    return params.node.group ? 50 : null;
  }

  gotoPage(pageValue :string) :void {
    this.stockFacadeService.gotoPage(pageValue);
  }

  handleGoToPageInput(inputElement :HTMLInputElement) :void {
    // COMPONENT LEVEL LOGIC
    const inputElementValue :number = Number(inputElement.value);
    if (inputElementValue === 0) {
      inputElement.value = '1';
    }
    if (inputElementValue > this.pagination.last_page) {
      inputElement.value = this.pagination.last_page.toString();
    }
  }

  onColumnMoved(event :ColumnMovedEvent) :void {
    if (event.finished) {
      localStorage.setItem(
        LocalStorageKeys.STOCK_AG_STATE,
        JSON.stringify({
          filterModel: this.currentFilterModel,
          columnState: this.currentColumnState,
        }),
      );
    }
  }

  onRefetchTableData() :void {
    this.stockFacadeService.getStocks(this.currentQueryParams);
  }

  getRowStyle(params :any) :any {
    let backgroundColor = null;
    if (params.data?.stockStatus === EStockStatusValue.REORDER_LEVEL) {
      backgroundColor = '#fdf0c4';
    } else if (params.data?.stockStatus === EStockStatusValue.MINIMUM_STOCK) {
      backgroundColor = '#fdd8dc';
    }

    return backgroundColor ? { background: backgroundColor } : null;
  }

  private _subscribeToColumnFilters() :void {
    this.stockFacadeService.columnFilters$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((filters) => {
        this.gridOptions?.api?.setFilterModel(filters.filterModel ?? {});
        this.gridOptions?.columnApi?.applyColumnState(
          filters.columnState ?? {
            defaultState: { sort: null },
          },
        );
      });
  }

  private applyStoredState() :void {
    if (this.storedPageState?.columnState) {
      const modifiedColumnState :any = {};

      Object.keys(this.storedPageState.columnState).forEach((key) => {
        modifiedColumnState[key] = {
          ...this.storedPageState.columnState[key],
          sort: null,
        };
      });

      this.gridColumnApi.applyColumnState({
        state: modifiedColumnState,
        applyOrder: true,
      });
    }

    if (this.storedPageState?.filterModel) {
      this.gridApi.setFilterModel(this.storedPageState.filterModel);
    }
  }

  private setRowGroupStatus(data :{ columnField :string; status :boolean }) :void {
    const { columnField, status } = data;
    const columnDefs = this.gridApi.getColumnDefs() as ColDef[];

    for (const colDef of columnDefs) {
      if (colDef.field === columnField) {
        colDef.rowGroup = status;
        break;
      }
    }

    this.gridOptions.api.setColumnDefs(columnDefs);
  }

  private subscribeToStocks() :void {
    this.stockFacadeService.stocks$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((stocks) => {
        if (this.groupBy && this.gridApi) {
          this.updateGroupedRows();
        }
      });
  }

  private initGroupState() :void {
    const groupState = localStorage.getItem(LocalStorageKeys.STOCK_AG_GROUP_STATE);
    if (groupState) {
      this.stockFacadeService.setGroupState(groupState);
    }
  }

  private removeColumnByField(field :string) :void {
    const columnDefs = this.gridOptions.columnDefs;
    const newColumnDefs = columnDefs.filter((col :any) => col.field !== field);
    this.gridApi.setColumnDefs(newColumnDefs);
  }

  private updateGroupedRows() :void {
    const colDefs = this.gridApi.getColumnDefs() as ColDef[];
    const newColDefs = colDefs.map((newColDef, index) => {
      const colDef = colDefs.find((col :any) => col.field === newColDef.field);
      if (newColDef.rowGroup) {
        return {
          ...newColDef,
          hide: true,
        };
      }

      return {
        ...newColDef,
        hide: colDef?.hide === undefined ? false : colDef.hide,
      };
    });
    this.gridOptions.autoGroupColumnDef = {
      headerName: 'Grouped By',
      fields: this.currentGroupOption?.field,
      width: 250,
      maxWidth: 300,
      flex: 1,
      cellRenderer: StockGroupColumnComponent,
      cellRendererParams: (params :any) => ({
        params: params,
        groups: this.groups,
      }),
    };

    const groupByColumnExist = colDefs.find(
      (colDef) => colDef.field === this.currentGroupOption?.field,
    );
    if (!groupByColumnExist) {
      newColDefs.push({
        headerName: this.currentGroupOption?.headerName,
        field: this.currentGroupOption?.field,
        rowGroup: true,
        hide: true,
        lockVisible: true,
        filter: false,
        sortable: false,
        valueGetter: (params :any) :string => {
          const value = params.data ? params.data[this.groupBy] : null;
          if (!value) {
            return '';
          }
          const group = this.groups.find((g) => value === g.label);

          return group?.label || value;
        },
      });
    }
    this.gridApi.refreshCells();
    this.gridApi.setColumnDefs(newColDefs);
  }

  private updateWarehouseGroupColumns() :void {
    if (!this.gridApi) {
      return;
    }

    const columnState = this.storedPageState?.columnState;

    const warehouseGroupColumn = this.columnDefs.find(
      (col) => col.field === this.fields.WAREHOUSE_GROUP,
    );
    if (warehouseGroupColumn) {
      warehouseGroupColumn.filterParams = {
        values: this.warehouseGroupIds,
        valueFormatter: (data :any) => {
          const warehouseGroup = this.warehouseGroups.find(
            (row :IWarehouseGroup) => row.id === data.value,
          );

          return warehouseGroup?.label;
        },
      };
    }

    this.gridApi.setColumnDefs(this.columnDefs);

    this.gridColumnApi.applyColumnState({
      state: this.storedPageState?.columnState,
      applyOrder: true,
    });
  }

  private initColumnDefs() :void {
    if (!this.storedPageState?.columnState?.length) {
      return;
    }

    const columnDefs = this.columnDefs;
    const columnState = this.storedPageState?.columnState;

    columnDefs.forEach((colDef :any) => {
      const column = columnState.find(
        (column :any) => column.colId === colDef.field,
      );

      if (!column) {
        columnState.push({
          colId: colDef.field,
          width: colDef.minWidth ?? 200,
          hide: colDef.hide ?? false,
          pinned: colDef.pinned ?? null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: colDef.rowGroup ?? false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        });
      }
    });

    localStorage.setItem(
      LocalStorageKeys.STOCK_AG_STATE,
      JSON.stringify({
        ...this.storedPageState,
        columnState,
      }),
    );
  }

  get warehouseGroupIds() :number[] {
    return this.warehouseGroups.map((row :IWarehouseGroup) => row.id);
  }

  get currentFilterModel() :any {
    return this.gridApi.getFilterModel();
  }

  get currentColumnState() :any {
    return this.gridColumnApi?.getColumnState();
  }

  get goToPageValue() :number {
    if (this.pagination.pageIndex != null) {
      return this.pagination.pageIndex + 1;
    } else {
      return this.pagination.current_page || 1;
    }
  }

  get stockStatusDisplay() :string {
    return (
      this.capitalizePipe.transform(
        this.currentQueryParams[this.fields.STOCK_STATUS],
        true,
      ) ?? ''
    );
  }

  get stocks$() :Observable<IStock[]> {
    return this.stockFacadeService.stocks$;
  }

  get stocks() :IStock[] {
    return this.stockFacadeService.stocks;
  }

  get pagination() :IPagination {
    return this.stockFacadeService.stocksMeta;
  }

  get currentQueryParams() :any {
    return this.stockFacadeService.currentQueryParams;
  }

  get loadingStocks$() :Observable<boolean> {
    return this.stockFacadeService.loadingStocks$;
  }

  get storedPageState() :any {
    return this.stockFacadeService.storedPageState;
  }

  get currentGroupsTotalCount() :number {
    return this.groups.reduce((acc, curr) => acc + curr.count, 0);
  }

  get groups() :IStockGroup[] {
    return this.stockFacadeService.groups;
  }

  get groupBy() :string {
    return this.stockFacadeService.groupBy;
  }

  get currentGroupOption() :any {
    return this.groupBy
      ? this.groupByOptions.find((group) => group.field === this.groupBy)
      : '';
  }

  get selectedRows() :IStock[] {
    return this.stockFacadeService.selectedRows;
  }

  get selectedRowCount() :number {
    return this.selectedRows.length;
  }
}
